import { Col, Row } from "react-bootstrap";
import { LIST_MODE, LIST_PER_PAGE } from "@/constant/Lists/listMode";
import {
  countriesApi,
  countryManagementApi,
  currenciesApi,
  languagesApi,
} from "@/apis/apis";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useGet, usePost, usePut } from "@/apiHooks/HttpFunctions";

import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";
import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import RadioInput from "@/components/RadioInput/Index";
import RenderToast from "@/components/RenderToast";
import { booleanOptionFormOptions } from "@/constant/GeneralOptions/boolean";
import { formatDropdownItem } from "@/utils/helper";
import getCountryInitialValues from "@/data/RegionManagement/Countries/initial-values";
import getCountrySchema from "@/schema/RegionManagement/Countries/schema";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import useModalGetOptions from "@/CustomHooks/Modal/useModalGetOptions";
import {
  FEATURE_STATUS,
  featureStatusFormOptions,
} from "@/constant/GeneralOptions/feature-status";

const CountryModal = forwardRef((_, ref) => {
  const booleanOptionFormOptionsMap = booleanOptionFormOptions();
  const [modalState, setModalState] = useState({ isOpen: false, id: null });
  const [selectedCountry, setSelectedCountry] = useState(null);

  const showApi = modalState.id
    ? `${countryManagementApi}/${modalState.id}`
    : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });
  useEffect(() => {
    if (modalState.id) setSelectedCountry(showData?.data);
  }, [modalState.id, showData?.data]);

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Country ${action} successfully.`,
    });
  };

  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePut({
    apiURL: `${countryManagementApi}/${modalState.id}`,
    onSuccess: handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: countryManagementApi,
    onSuccess: handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getCountryInitialValues(modalState.id ? showData : null),
    validationSchema: getCountrySchema(),
    onSubmit: (values) => handleSubmit(values),
  });

  const baseQueryParams = `?mode=${LIST_MODE.SELECT}&perPage=${LIST_PER_PAGE}&filter[keyword]=`;
  const countryListApi = `${countriesApi}${baseQueryParams}`;
  const {
    loading: countryLoading,
    defaultOptions: countryDefaultOptions,
    debouncedFetchOptions: countryFetchOptions,
    getSelectedOption: countryGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: countryListApi,
    formikValue: formik.values.country_id,
    formikInitialValue: formik.initialValues.country_id,
    showData: !!modalState?.id && showData?.data?.country,
  });
  const defaultShipToQueryParams = `?mode=${LIST_MODE.SELECT}&perPage=${LIST_PER_PAGE}&filter[enable_personal_user]=true&filter[keyword]=`;
  const defaultShipToListApi = `${countryManagementApi}${defaultShipToQueryParams}`;
  const defaultShipToValueFormatter = useCallback(
    (item) => (item.country ? item.country?.code : null),
    []
  );
  const defaultShipToLabelFormatter = useCallback(
    (item) =>
      item.country ? `${item.country?.code} - ${item.country?.name}` : "",
    []
  );
  const showDataShipToCountry = useMemo(
    () =>
      showData?.data?.ship_to_location && {
        country: showData?.data?.ship_to_location,
      },
    [showData?.data?.ship_to_location]
  );
  const {
    defaultOptions: defaultShipToDefaultOptions,
    debouncedFetchOptions: defaultShipToFetchOptions,
    loading: defaultShipToLoading,
    getSelectedOption: defaultShipToGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: defaultShipToListApi,
    formikValue: formik.values.ship_to_location_code,
    formikInitialValue: formik.initialValues.ship_to_location_code,
    showData: !!modalState?.id && showDataShipToCountry,
    valueFormatter: defaultShipToValueFormatter,
    labelFormatter: defaultShipToLabelFormatter,
  });
  const codeValueFormatter = useCallback((item) => item?.code, []);
  const languageListApi = `${languagesApi}${baseQueryParams}`;
  const {
    defaultOptions: defaultLanguageDefaultOptions,
    debouncedFetchOptions: defaultLanguageFetchOptions,
    loading: defaultLanguageLoading,
    getSelectedOption: defaultLanguageGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: languageListApi,
    formikValue: formik.values.language_code,
    formikInitialValue: formik.initialValues.language_code,
    showData: !!modalState?.id && showData?.data?.language,
    valueFormatter: codeValueFormatter,
  });
  const currencyListApi = `${currenciesApi}${baseQueryParams}`;
  const {
    defaultOptions: defaultCurrencyDefaultOptions,
    debouncedFetchOptions: defaultCurrencyFetchOptions,
    loading: defaultCurrencyLoading,
    getSelectedOption: defaultCurrencyGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: currencyListApi,
    formikValue: formik.values.currency_code,
    formikInitialValue: formik.initialValues.currency_code,
    showData: !!modalState?.id && showData?.data?.currency,
    valueFormatter: codeValueFormatter,
  });

  const handleSubmit = (values) => {
    modalState.id ? saveEdit(values) : saveCreate(values);
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
    setSelectedCountry(null);
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={modalState.id ? "Edit Country" : "Add Country"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"region"}
            saveError={saveError}
            label="Region"
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <AsyncDropdown
            label="Country"
            placeholder="No country selected. Type to search..."
            defaultOptions={countryDefaultOptions}
            loadOptions={countryFetchOptions}
            isLoading={countryLoading}
            value={countryGetSelectedOption()}
            onChange={(selected) => {
              setSelectedCountry({ country: selected?.data });
              formik.setFieldValue("country_id", selected?.value || null);
              formik.setFieldValue(
                "country_phone_code",
                selected?.data?.phone_code
              );
              formik.setFieldValue("country_tld", selected?.data?.tld);
              formik.setFieldValue("country_code", selected?.data?.code);
              formik.setFieldValue(
                "country_code_alpha_3",
                selected?.data?.code_alpha_3
              );
              if (formik.values.enable_personal_user === FEATURE_STATUS.YES) {
                formik.setFieldValue(
                  "ship_to_location_code",
                  selected?.data?.code
                );
              }
            }}
            errorText={
              (formik.touched.country_id && formik.errors.country_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.country_id?.join(" "))
            }
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"customs_region"}
            saveError={saveError}
            label="Customs Region"
            required={false}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"country_phone_code"}
            saveError={saveError}
            label="Phone Code"
            disabled
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"country_tld"}
            saveError={saveError}
            label="TLD"
            disabled
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <RadioInput
            options={booleanOptionFormOptionsMap}
            formik={formik}
            saveError={saveError}
            fieldName="tld_owned"
            label="TLD Owned"
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName="country_code"
            saveError={saveError}
            label="2-Letter Code"
            disabled
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName="country_code_alpha_3"
            saveError={saveError}
            label="3-Letter Code"
            disabled
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <RadioInput
            options={featureStatusFormOptions}
            formik={formik}
            saveError={saveError}
            fieldName="enable_professional_user"
            label="Commercial User"
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <RadioInput
            options={featureStatusFormOptions}
            formik={formik}
            saveError={saveError}
            fieldName="enable_personal_user"
            label="Personal User"
            onChange={async (option) => {
              formik.setFieldValue("enable_personal_user", option.value);
              if (option.value === FEATURE_STATUS.YES) {
                formik.setFieldValue(
                  "ship_to_location_code",
                  formik.values.country_code || null
                );
                return;
              }
              formik.setFieldValue("ship_to_location_code", null);
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"vat_merchandise"}
            saveError={saveError}
            label="VAT Merchandise"
            type="number"
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"vat_e_service"}
            saveError={saveError}
            label="VAT E-Service"
            type="number"
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <AsyncDropdown
            required={false}
            label="Default Ship-to Location"
            placeholder="No default ship-to location selected. Type to search..."
            defaultOptions={defaultShipToDefaultOptions}
            loadOptions={defaultShipToFetchOptions}
            isLoading={defaultShipToLoading}
            value={
              formik.values.enable_personal_user === FEATURE_STATUS.YES
                ? formatDropdownItem(
                    selectedCountry,
                    defaultShipToLabelFormatter,
                    defaultShipToValueFormatter
                  )
                : defaultShipToGetSelectedOption()
            }
            onChange={(selected) => {
              formik.setFieldValue(
                "ship_to_location_code",
                selected?.value || null
              );
            }}
            errorText={
              (formik.touched.ship_to_location_code &&
                formik.errors.ship_to_location_code) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.ship_to_location_code?.join(
                  " "
                ))
            }
            isDisabled={
              formik.values.enable_personal_user === FEATURE_STATUS.YES
            }
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <AsyncDropdown
            required={false}
            label="Default Language"
            placeholder="No default language selected. Type to search..."
            defaultOptions={defaultLanguageDefaultOptions}
            loadOptions={defaultLanguageFetchOptions}
            isLoading={defaultLanguageLoading}
            value={defaultLanguageGetSelectedOption()}
            onChange={(selected) => {
              formik.setFieldValue("language_code", selected?.value || null);
            }}
            errorText={
              (formik.touched.language_code && formik.errors.language_code) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.language_code?.join(" "))
            }
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <AsyncDropdown
            required={false}
            label="Default Currency"
            placeholder="No default currency selected. Type to search..."
            defaultOptions={defaultCurrencyDefaultOptions}
            loadOptions={defaultCurrencyFetchOptions}
            isLoading={defaultCurrencyLoading}
            value={defaultCurrencyGetSelectedOption()}
            onChange={(selected) => {
              formik.setFieldValue("currency_code", selected?.value || null);
            }}
            errorText={
              (formik.touched.currency_code && formik.errors.currency_code) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.currency_code?.join(" "))
            }
          />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default CountryModal;
